// Weg und Tageszeit der Fahrt (B24, Erste Fassung, Runde 7): nach public/art/fahrt/fahrt-spiel.json des Grafik-Leads.
// Zwei Uhren: der Weg w (Bildpixel, die die Ebene vorn zurücklegt) bestimmt, wo Ebenen, Anlegestellen und Durchlass
// liegen; die Tageszeit t hängt nur vom Weg ab und bestimmt Ort, Kulisse, Wetter und Licht. Reine Rechnung, ohne DOM,
// damit sie in Tests läuft.

export type Strecke = { sekunden: number; weg: number; ablegen: number; anlegen: number };
/** takt aus durchfahrt.json */
export type Takt = { halten: number; uebergang: number; nacht: number; kulisse: number; wetter: number };

/** Reisetempo v in Bildpixeln je Sekunde: eine Strecke mit Ab- und Anlegen dauert genau strecke.sekunden */
export const reiseTempo = (s: Strecke): number => s.weg / (s.sekunden - s.ablegen / 2 - s.anlegen / 2);

/**
 * Eine Fahrt von Halt zu Halt (oder über mehrere): Länge L, Ablegen a (0, wenn das Boot schon fährt), Anlegen d (0
 * beim Vorbeitreiben), Tempo v. Das Tempo steigt und fällt mit der Kurve x² · (3 − 2x)
 */
export type Leg = { L: number; a: number; d: number; v: number };

/** Wie lange eine Fahrt dauert */
export const legSeconds = (g: Leg): number => g.L / g.v + g.a / 2 + g.d / 2;

/** Weg seit dem Halt nach τ Sekunden (Formel aus den Hinweisen „Tempo einer Strecke“) */
export function legDistance(g: Leg, tau: number): number {
  const D = legSeconds(g);
  if (tau <= 0) return 0;
  if (tau >= D) return g.L;
  if (g.d > 0 && tau > D - g.d) {
    const y = (D - tau) / g.d;
    return g.L - g.v * g.d * (y ** 3 - y ** 4 / 2);
  }
  if (g.a > 0 && tau < g.a) {
    const x = tau / g.a;
    return g.v * g.a * (x ** 3 - x ** 4 / 2);
  }
  return g.v * (g.a / 2 + tau - g.a);
}

/** Die Mitte von Ort k auf der Zeitachse der Bilder: T_k = halten / 2 + k · (halten + übergang) */
export const ortMitte = (takt: Takt, k: number): number => takt.halten / 2 + k * (takt.halten + takt.uebergang);

/** Ortswechsel i (1, 2) liegt in Strecke s_i = abrunden(i · n / 3) + 1 */
export const wechselStrecke = (n: number, i: number): number => Math.floor((i * n) / 3) + 1;

/** Tageszeit t zum Weg w an einem Tag mit n Strecken (Hinweis „Tageszeit“) */
export function dayTime(w: number, n: number, takt: Takt, L: number, orte = 3): number {
  const s = Math.max(1, Math.min(n, Math.floor(w / L) + 1));
  const u = Math.max(0, Math.min(1, (w - (s - 1) * L) / L));
  const wechsel = Array.from({ length: orte - 1 }, (_, i) => wechselStrecke(n, i + 1));
  const i = wechsel.indexOf(s);
  if (i >= 0) {
    const a = ortMitte(takt, i);
    const b = ortMitte(takt, i + 1);
    return a + (b - a) * (u + Math.sin(2 * Math.PI * u) / (4 * Math.PI));
  }
  if (s === 1) return ortMitte(takt, 0) * u;
  const k = wechsel.filter((x) => x < s).length;
  return ortMitte(takt, k);
}

/** Wo man Ort k sieht, und die Mitte davon (mitten[k]): Ort 0 bis (s_1 − ½) · L, Ort 1 bis (s_2 − ½) · L, Ort 2 bis n · L */
export function ortWege(n: number, L: number, orte = 3): { von: number; bis: number; mitte: number }[] {
  const grenzen = [0, ...Array.from({ length: orte - 1 }, (_, i) => (wechselStrecke(n, i + 1) - 0.5) * L), n * L];
  return Array.from({ length: orte }, (_, k) => {
    const von = grenzen[k]!;
    const bis = grenzen[k + 1]!;
    return { von, bis, mitte: (von + bis) / 2 };
  });
}
